import { useEffect, useState } from "react";
import { ElementData, EptForm } from "../form-builder/utils/types";
import { api, apiVanilla, RouterOutputs } from "@/lib/trpc/client";
import { AnonymousCredential, ContainerClient } from "@azure/storage-blob";
import { v4 as uuid } from "uuid";
import { getVisibleDynamicElements } from "./utils/element-validators";
import { findValidatorWithType } from "@ept/shared/src/validators";
import { toast } from "react-toastify";
import { translations } from "@/messages";
import { SubmittedTableValue, SubmittedElement, SubmittedTableElement } from "./types";

export const useActions = (
  formFields: RouterOutputs["formManagementRouter"]["getFormDataBySlug"],
) => {
  const [elements, setElements] = useState<{
    [key: string]: ElementData["element"] & { elementSectionId: string };
  }>();
  const [uploadFolderUrl, setUploadFolderUrl] = useState<string | null>(null);

  const uploadFinished = api.uploadRouter.uploadFinish.useMutation();

  const getUploadFolderUrl = async () => {
    if (uploadFolderUrl) {
      return uploadFolderUrl;
    }

    const url = await apiVanilla.uploadRouter.getFolderUploadUrl.query({});
    setUploadFolderUrl(url);
    return url;
  };

  useEffect(() => {
    if (!formFields) {
      return;
    }

    const extractedElements = formFields.sections
      .flatMap((section) =>
        section.section.elements.flatMap((sectionElement) => ({
          ...sectionElement.element,
          id: sectionElement.id,
          elementSectionId: sectionElement.id,
        })),
      )
      .flat();

    setElements(
      extractedElements.reduce(
        (prevElements, element) => ({ ...prevElements, [element.id!]: element }),
        {},
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formFields?.id]);

  if (!formFields) {
    return null;
  }

  const handleMultipleFilesUpload = async (
    containerClient: ContainerClient,
    inputFiles: FileList,
    locale: keyof typeof translations,
    uploadFolderUrl: string,
  ) => {
    if (!inputFiles.length) {
      return null;
    }

    try {
      return Promise.all(
        Object.values(inputFiles).map(async (input) => {
          const uniqueFileName = `${uuid()}-${input.name}`;
          const blockClient = containerClient.getBlockBlobClient(uniqueFileName);
          const response = await blockClient.uploadData(input);

          if (response.errorCode) {
            toast.error(translations[locale]["formView"]["cannotUpload"]);
            throw new Error(response.errorCode);
          }

          const uploadedFile = await uploadFinished.mutateAsync({
            fileName: uniqueFileName,
            originalFileName: input.name,
          });

          return uploadedFile.id;
        }),
      );
    } catch (error) {
      return null;
    }
  };

  const getRequestValue = async (
    elementId: string,
    input: SubmittedElement,
    locale: keyof typeof translations,
  ): Promise<{
    elementSectionId: string;
    type: string;
    value: string | SubmittedTableValue;
    attachments?: string[] | undefined;
  } | null> => {
    if (!input || !elements || !elements[elementId]) {
      return null;
    }

    const element = elements[elementId]!;
    const url = await getUploadFolderUrl();

    if (!url) {
      throw new Error("Failed to fetch upload folder URL");
    }

    const containerClient = new ContainerClient(url, new AnonymousCredential(), {
      retryOptions: { maxTries: 5 },
    });

    switch (element.type) {
      case "DATE":
        return {
          elementSectionId: element.elementSectionId,
          type: element.type.toString(),
          value: input as string,
        };
      case "FILE":
        const uploadedFile = await handleMultipleFilesUpload(
          containerClient,
          [input] as unknown as FileList,
          locale,
          url,
        );
        return uploadedFile
          ? {
              elementSectionId: element.elementSectionId,
              type: element.type.toString(),
              value: uploadedFile.join(","),
              attachments: uploadedFile,
            }
          : null;
      case "FILE_MULTIPLE":
        const uploadedFiles = await handleMultipleFilesUpload(
          containerClient,
          input as unknown as FileList,
          locale,
          url,
        );
        return uploadedFiles
          ? {
              elementSectionId: element.elementSectionId,
              type: element.type.toString(),
              value: uploadedFiles.join(","),
              attachments: uploadedFiles,
            }
          : null;
      case "TABLE":
        return {
          elementSectionId: element.elementSectionId,
          type: element.type.toString(),
          value: (input as SubmittedTableElement).flatMap((tableInputField, index) => {
            const columnIds = Object.keys(tableInputField);

            return columnIds.flatMap((id) => ({
              columnId: id,
              rowNumber: index,
              value: tableInputField[id]!,
            }));
          }),
        };
      default:
        return {
          elementSectionId: element.elementSectionId,
          type: element.type.toString(),
          value: input.toString(),
        };
    }
  };

  const formatElementsForRequest = async (
    inputs: { [elementId: string]: SubmittedElement },
    locale: keyof typeof translations,
  ) => {
    const fields = Object.keys(inputs).filter(
      (elementId) => inputs[elementId] && inputs[elementId] !== false,
    );
    const requestFields: {
      type: string;
      value: string | SubmittedTableValue;
      elementSectionId: string;
      attachments?: string[] | undefined;
    }[] = [];

    const visibleDynamicElements = getVisibleDynamicElements(inputs, formFields);

    for (const elementId of fields) {
      const elementData = elements![elementId];
      if (
        findValidatorWithType(elementData?.validators || [], "CLIENT_SIDE_DYNAMIC") &&
        !visibleDynamicElements?.find((visibleElement) => visibleElement.id === elementData?.id)
      ) {
        continue;
      }
      const elementValue = await getRequestValue(elementId, inputs[elementId], locale);
      if (!elementValue) {
        continue;
      }

      requestFields.push(elementValue);
    }

    return {
      formId: formFields.id!,
      fields: requestFields,
    };
  };

  return { formatElementsForRequest };
};
